import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search, Video, Trash2, Copy, X, Calendar, ExternalLink, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';
import Seo from '@/components/shared/Seo';

const STATUS_TABS = ['all', 'scheduled', 'live', 'completed', 'cancelled', 'missed'];

const StatBox = ({ label, value, tone = 'gray' }) => (
  <div className={`rounded-xl border p-4 ${tone === 'red' ? 'bg-red-50 border-red-100' : 'bg-white border-gray-200'}`}>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

const CreateModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    student_name: '', student_email: '',
    tutor_name: '', tutor_email: '',
    subject: 'IELTS Writing',
    start_time: '', end_time: '',
    payment_status: 'unpaid',
    payment_required: false,
    notes_for_admin: '',
  });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
      };
      const r = await classroomApi.adminCreateBooking(payload);
      toast.success('Classroom booking created');
      onCreated(r);
      onClose();
    } catch (err) { toast.error(err.message || 'Could not create'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-classes-create-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold">Winhouse Classroom</div>
            <h2 className="text-xl font-bold">New booking</h2>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-gray-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Student name" value={form.student_name} onChange={(v) => set('student_name', v)} required tid="cm-student-name" />
            <Input label="Student email" type="email" value={form.student_email} onChange={(v) => set('student_email', v)} required tid="cm-student-email" />
            <Input label="Tutor name" value={form.tutor_name} onChange={(v) => set('tutor_name', v)} required tid="cm-tutor-name" />
            <Input label="Tutor email" type="email" value={form.tutor_email} onChange={(v) => set('tutor_email', v)} required tid="cm-tutor-email" />
            <Input label="Subject" value={form.subject} onChange={(v) => set('subject', v)} required tid="cm-subject" />
            <Select label="Payment status" value={form.payment_status} onChange={(v) => set('payment_status', v)}
              options={[{ v: 'unpaid', l: 'Unpaid' }, { v: 'paid', l: 'Paid' }, { v: 'waived', l: 'Waived (admin approved)' }]} tid="cm-payment" />
            <Input label="Start time" type="datetime-local" value={form.start_time} onChange={(v) => set('start_time', v)} required tid="cm-start" />
            <Input label="End time" type="datetime-local" value={form.end_time} onChange={(v) => set('end_time', v)} required tid="cm-end" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold block mb-1.5">Internal notes</label>
            <textarea value={form.notes_for_admin} onChange={(e) => set('notes_for_admin', e.target.value)}
              rows={2} className="w-full border border-gray-200 rounded-xl p-3 text-sm" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer border border-gray-200 rounded-xl p-3 hover:bg-gray-50">
            <input type="checkbox" checked={form.payment_required}
              onChange={(e) => set('payment_required', e.target.checked)}
              data-testid="cm-payment-required" className="h-4 w-4 accent-red-700" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">Require payment before access</div>
              <div className="text-[11px] text-gray-500">When ON, the classroom shows an Awaiting Payment screen until payment_status is set to <code>paid</code> or <code>waived</code>.</div>
            </div>
          </label>
          <button type="submit" disabled={busy}
            className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="cm-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create booking
          </button>
        </form>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = 'text', required, tid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold block mb-1.5">{label}</label>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      data-testid={tid}
      className="w-full h-11 border border-gray-200 rounded-xl px-3 text-sm outline-none focus:border-red-600" />
  </div>
);

const Select = ({ label, value, onChange, options, tid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold block mb-1.5">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={tid}
      className="w-full h-11 border border-gray-200 rounded-xl px-3 text-sm bg-white outline-none focus:border-red-600">
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const copyToClipboard = async (text, label) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
  catch (_e) { toast.error('Copy failed'); }
};

const AdminClassesPage = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, a, list] = await Promise.all([
        classroomApi.adminStats().catch(() => null),
        classroomApi.adminAnalytics().catch(() => null),
        classroomApi.adminListBookings(tab === 'all' ? {} : { status: tab }),
      ]);
      setStats(s);
      setAnalytics(a);
      setItems(list.items || []);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        toast.error('Please sign in as admin');
        window.location.assign('/admin/login');
        return;
      }
      toast.error(e.message || 'Could not load');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((it) =>
      it.subject?.toLowerCase().includes(s) ||
      it.student_name?.toLowerCase().includes(s) ||
      it.tutor_name?.toLowerCase().includes(s) ||
      it.student_email?.toLowerCase().includes(s) ||
      it.tutor_email?.toLowerCase().includes(s),
    );
  }, [items, q]);

  const remove = async (id) => {
    if (!window.confirm('Delete this booking and all associated data?')) return;
    try {
      await classroomApi.adminDeleteBooking(id);
      setItems((prev) => prev.filter((x) => x.booking_id !== id));
      toast.success('Deleted');
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const setStatus = async (it, status) => {
    try {
      const r = await classroomApi.adminUpdateBooking(it.booking_id, { status });
      setItems((prev) => prev.map((x) => x.booking_id === it.booking_id ? r : x));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(e.message || 'Update failed'); }
  };

  const resend = async (it, role) => {
    try {
      const r = await classroomApi.adminResendInvite(it.booking_id, role);
      const block = r?.results?.[role];
      if (block?.status === 'sent') {
        toast.success(`${role === 'tutor' ? 'Tutor' : 'Student'} email re-sent · id=${(block.email_id || '').slice(-8)}`);
      } else if (block?.status === 'skipped') {
        toast.message(block.error_message || `${role} email not configured yet.`);
      } else {
        toast.error(block?.error_message || 'Resend failed');
      }
      // Patch the local row's invites status without a full reload
      setItems((prev) => prev.map((x) => x.booking_id === it.booking_id
        ? { ...x, invites: { ...(x.invites || {}), [role]: block } }
        : x));
    } catch (e) { toast.error(e.message || 'Resend failed'); }
  };

  const inviteBadge = (block, label) => {
    if (!block) return null;
    const sent = block.status === 'sent';
    const skipped = block.status === 'skipped';
    const tone = sent ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                 : skipped ? 'text-amber-700 bg-amber-50 border-amber-200'
                 : 'text-red-700 bg-red-50 border-red-200';
    const Icon = sent ? CheckCircle2 : AlertCircle;
    const stateLabel = sent ? 'sent' : skipped ? 'pending' : 'failed';
    const tip = block.error_message
      || (block.last_sent_at ? `Last sent ${new Date(block.last_sent_at).toLocaleString()}` : '');
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${tone}`}
            title={tip}>
        <Icon className="h-3 w-3" />
        {label} {stateLabel}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-classes-page">
      <Seo title="Classes · Admin · Winhouse" canonical="/admin/classes" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">Admin</div>
            <h1 className="text-3xl font-bold tracking-tight">Winhouse Classes</h1>
            <p className="text-gray-600 text-sm mt-1">Schedule classes, share join links, review attendance.</p>
          </div>
          <div className="flex gap-2">
            <a href="/admin/classroom-settings"
              className="h-11 px-5 rounded-full border border-gray-300 text-sm font-semibold flex items-center gap-1.5 hover:bg-white">
              Settings
            </a>
            <button onClick={() => { window.location.href = '/admin/dashboard?tab=course-bookings'; }}
              className="h-11 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold flex items-center gap-1.5"
              data-testid="admin-classes-new"
              title="New schedules are now created in Course Bookings">
              <Plus className="h-4 w-4" /> Create Course Booking
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <StatBox label="Total"     value={stats.counts.total} />
            <StatBox label="Scheduled" value={stats.counts.scheduled} />
            <StatBox label="Live"      value={stats.counts.live} tone="red" />
            <StatBox label="Completed" value={stats.counts.completed} />
            <StatBox label="Missed"    value={stats.counts.missed} />
            <StatBox label="Cancelled" value={stats.counts.cancelled} />
          </div>
        )}

        {analytics && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6" data-testid="admin-classes-analytics">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Class analytics</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatBox label="Active tutors"     value={analytics.active_tutors} />
              <StatBox label="Active students"   value={analytics.active_students} />
              <StatBox label="Avg duration"      value={`${analytics.avg_duration_min}m`} />
              <StatBox label="Attendance"        value={`${analytics.attendance_pct}%`} />
              <StatBox label="No-show rate"      value={`${analytics.no_show_pct}%`} tone={analytics.no_show_pct > 10 ? 'red' : 'gray'} />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {STATUS_TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`h-9 px-4 rounded-full text-xs font-semibold uppercase tracking-wider border ${tab === t ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              data-testid={`admin-classes-tab-${t}`}>
              {t}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 border border-gray-200 rounded-full px-3 bg-white">
            <Search className="h-4 w-4 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, name, email…"
              className="h-9 outline-none text-sm bg-transparent w-56" data-testid="admin-classes-search" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-gray-500">
              <Calendar className="h-6 w-6 mx-auto text-gray-300 mb-3" />
              <div className="text-sm font-semibold">No bookings in this view</div>
              <div className="text-xs mt-1">Click <span className="font-semibold">New booking</span> to schedule one.</div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100" data-testid="admin-classes-list">
              {filtered.map((it) => (
                <div key={it.booking_id} className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${
                        it.status === 'live' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                        it.status === 'scheduled' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                        it.status === 'completed' ? 'bg-gray-100 text-gray-700 border-gray-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}>{it.status}</span>
                      <span className="text-[11px] uppercase tracking-wider text-gray-500">{it.payment_status}</span>
                    </div>
                    <div className="font-bold text-sm text-gray-900">{it.subject}</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {it.tutor_name} ({it.tutor_email}) ↔ {it.student_name} ({it.student_email})
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      {new Date(it.start_time).toLocaleString()} → {new Date(it.end_time).toLocaleTimeString()}
                    </div>
                    {it.invites && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {inviteBadge(it.invites.tutor, 'Tutor')}
                        {inviteBadge(it.invites.student, 'Student')}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 lg:w-auto">
                    <button onClick={() => resend(it, 'tutor')}
                      className="h-9 px-3 rounded-full border border-gray-300 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-gray-50"
                      data-testid={`admin-classes-resend-tutor-${it.booking_id}`}>
                      <Mail className="h-3.5 w-3.5" /> Resend tutor
                    </button>
                    <button onClick={() => resend(it, 'student')}
                      className="h-9 px-3 rounded-full border border-gray-300 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-gray-50"
                      data-testid={`admin-classes-resend-student-${it.booking_id}`}>
                      <Mail className="h-3.5 w-3.5" /> Resend student
                    </button>
                    <button onClick={() => copyToClipboard(window.location.origin + it.tutor_join_url, 'Tutor link')}
                      className="h-9 px-3 rounded-full border border-gray-300 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-gray-50"
                      data-testid={`admin-classes-copy-tutor-${it.booking_id}`}>
                      <Copy className="h-3.5 w-3.5" /> Tutor link
                    </button>
                    <button onClick={() => copyToClipboard(window.location.origin + it.student_join_url, 'Student link')}
                      className="h-9 px-3 rounded-full border border-gray-300 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 hover:bg-gray-50"
                      data-testid={`admin-classes-copy-student-${it.booking_id}`}>
                      <Copy className="h-3.5 w-3.5" /> Student link
                    </button>
                    <a href={it.tutor_join_url} target="_blank" rel="noreferrer"
                      className="h-9 px-3 rounded-full bg-black hover:bg-gray-900 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1"
                      data-testid={`admin-classes-open-${it.booking_id}`}>
                      <Video className="h-3.5 w-3.5" /> Open
                    </a>
                    <div className="relative">
                      <select value={it.status} onChange={(e) => setStatus(it, e.target.value)}
                        className="h-9 px-3 rounded-full border border-gray-300 text-[11px] font-semibold uppercase tracking-wider bg-white">
                        <option value="scheduled">Scheduled</option>
                        <option value="live">Live</option>
                        <option value="completed">Completed</option>
                        <option value="missed">Missed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <button onClick={() => remove(it.booking_id)}
                      className="h-9 w-9 rounded-full border border-gray-300 text-red-700 hover:bg-red-50 flex items-center justify-center"
                      data-testid={`admin-classes-delete-${it.booking_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateModal open={creating} onClose={() => setCreating(false)} onCreated={(it) => setItems((prev) => [it, ...prev])} />
    </div>
  );
};

export default AdminClassesPage;
